import { useMemo } from 'react';
import type { Choice, TestResult } from '../../types/test';

export interface TestChartProps {
  chartStyle: 0 | 1 | 2; // 0: 円グラフ, 1: 棒グラフ（横）, 2: 棒グラフ（縦）
  choices: Choice[];
  results: TestResult[];
}

/**
 * Color palette for chart bars/segments.
 */
const CHART_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
] as const;

/**
 * Get color class for a choice index.
 */
function getColorClass(index: number): string {
  const colorIndex = index % CHART_COLORS.length;
  return CHART_COLORS[colorIndex] ?? 'bg-blue-500';
}

/**
 * Get result for a specific choice.
 */
function getResultForChoice(
  choiceId: number,
  results: TestResult[],
): TestResult | undefined {
  return results.find((r) => r.choiceId === choiceId);
}

interface ChartDataItem {
  choice: Choice;
  result: TestResult | undefined;
  colorClass: string;
  index: number;
}

/**
 * HorizontalBarChart - Renders a horizontal bar chart.
 */
function HorizontalBarChart({
  data,
}: {
  data: ChartDataItem[];
}) {
  return (
    <div className="mini-bar-chart-horizontal" role="img" aria-label="解答分布（横棒グラフ）">
      {data.map(({ choice, result, index }) => {
        const percentage = result?.percentage ?? 0;
        const choiceIndex = index + 1;

        return (
          <div key={choice.id} className="mini-bar-row">
            <span className="mini-bar-label">{choiceIndex}</span>
            <div className="mini-bar-track">
              <div
                className="mini-bar-fill"
                style={{ width: `${percentage}%` }}
                role="progressbar"
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${choice.choice}: ${percentage.toFixed(1)}%`}
              />
            </div>
            <span className="mini-bar-value">{Math.round(percentage)}%</span>
          </div>
        );
      })}
      {/* X軸の目盛りラベル */}
      <div className="mini-bar-axis-horizontal" aria-hidden="true">
        <span />
        <div className="mini-bar-axis-scale">
          {[20, 40, 60, 80].map((tick) => (
            <span
              key={tick}
              className="mini-bar-axis-tick"
              style={{ left: `${tick}%` }}
            >
              {tick}
            </span>
          ))}
          <span className="mini-bar-axis-tick mini-bar-axis-tick-100">100</span>
        </div>
        <span />
      </div>
    </div>
  );
}

/**
 * VerticalBarChart - Renders a vertical bar chart.
 */
function VerticalBarChart({
  data,
}: {
  data: ChartDataItem[];
}) {
  const maxPercentage = Math.max(
    ...data.map((d) => d.result?.percentage ?? 0),
    1,
  );

  return (
    <div
      className="flex items-end justify-center gap-4"
      role="img"
      aria-label="解答分布（縦棒グラフ）"
    >
      {data.map(({ choice, result, colorClass }, index) => {
        const percentage = result?.percentage ?? 0;
        const count = result?.count ?? 0;
        const heightPercent = (percentage / maxPercentage) * 100;

        return (
          <div
            key={choice.id}
            className="flex flex-col items-center gap-2"
            style={{ minWidth: '60px', maxWidth: '100px' }}
          >
            {/* Percentage label */}
            <span className="text-sm font-medium text-gray-900">
              {percentage.toFixed(1)}%
            </span>
            
            {/* Bar container */}
            <div
              className="relative w-full"
              style={{ height: '150px' }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${choice.choice}: ${percentage.toFixed(1)}%`}
            >
              <div className="absolute inset-x-0 bottom-0 rounded-t-md bg-gray-200" style={{ height: '100%' }}>
                <div
                  className={`absolute inset-x-0 bottom-0 rounded-t-md transition-all duration-500 ${colorClass}`}
                  style={{ height: `${heightPercent}%` }}
                />
              </div>
            </div>
            
            {/* Choice label */}
            <div className="text-center">
              <span className="block text-xs font-medium text-gray-700">
                選択肢{index + 1}
              </span>
              <span className="block text-xs text-gray-500">
                ({count}人)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/**
 * PieChart - Renders a pie chart using CSS conic-gradient.
 */
function PieChart({
  data,
}: {
  data: ChartDataItem[];
}) {
  // Build conic gradient stops
  const gradientStops = useMemo(() => {
    let currentAngle = 0;
    const stops: string[] = [];
    
    const pieColors = [
      '#3b82f6', // blue-500
      '#22c55e', // green-500
      '#eab308', // yellow-500
      '#a855f7', // purple-500
      '#ec4899', // pink-500
      '#6366f1', // indigo-500
      '#14b8a6', // teal-500
      '#f97316', // orange-500
    ];

    data.forEach(({ result }, index) => {
      const percentage = result?.percentage ?? 0;
      if (percentage <= 0) return;
      const angle = (percentage / 100) * 360;
      const color = pieColors[index % pieColors.length];
      
      stops.push(`${color} ${currentAngle}deg ${currentAngle + angle}deg`);
      currentAngle += angle;
    });

    if (stops.length === 0) {
      return '#e5e7eb 0deg 360deg';
    }

    // Fill remaining with gray if total < 100%
    if (currentAngle < 359.99) {
      stops.push(`#e5e7eb ${currentAngle}deg 360deg`);
    }

    return stops.join(', ');
  }, [data]);

  return (
    <div
      className="flex flex-col items-center gap-4"
      role="img"
      aria-label="解答分布（円グラフ）"
    >
      {/* Pie chart */}
      <div
        className="h-48 w-48 rounded-full shadow-inner"
        style={{
          background: `conic-gradient(${gradientStops})`,
        }}
        aria-hidden="true"
      />

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3">
        {data.map(({ choice, result, colorClass }, index) => {
          const percentage = result?.percentage ?? 0;
          const count = result?.count ?? 0;

          return (
            <div
              key={choice.id}
              className="flex items-center gap-1.5 text-sm"
            >
              <span
                className={`h-3 w-3 rounded-sm ${colorClass}`}
                aria-hidden="true"
              />
              <span className="text-gray-700">
                選択肢{index + 1}: {percentage.toFixed(1)}% ({count}人)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * TestChart - Displays answer distribution chart for test questions.
 *
 * Supports multiple chart types (管理画面の ChartStyle と統一):
 * - 0: 円グラフ (Pie chart)
 * - 1: 棒グラフ（横） (Horizontal bar chart)
 * - 2: 棒グラフ（縦） (Vertical bar chart)
 *
 * Validates: Requirements 6.6, 6.7
 */
export function TestChart({ chartStyle, choices, results }: TestChartProps) {
  // Sort choices by sort_key
  const sortedChoices = useMemo(
    () => [...choices].sort((a, b) => a.sort_key - b.sort_key),
    [choices],
  );

  // Prepare chart data
  const chartData: ChartDataItem[] = useMemo(
    () =>
      sortedChoices.map((choice, index) => ({
        choice,
        result: getResultForChoice(choice.id, results),
        colorClass: getColorClass(index),
        index,
      })),
    [sortedChoices, results],
  );

  if (choices.length === 0) {
    return null;
  }

  return (
    <div className="chart-container">
      <h4 className="chart-title">解答分布</h4>
      
      {chartStyle === 0 && <PieChart data={chartData} />}
      {chartStyle === 1 && <HorizontalBarChart data={chartData} />}
      {chartStyle === 2 && <VerticalBarChart data={chartData} />}
    </div>
  );
}
